// =============================================================================
// VandieZ agent-web -- Profile (/profile)
// =============================================================================
// FSD p58: company details with GST, business summary, payment settlement.
//
// GSTIN AND PAN ARE EDITABLE, AND THAT IS NOT AN OVERSIGHT. Changing one on
// an already-approved account raises an admin review flag (D-20, migration
// 0092) while leaving the account trading, because the common case is a
// typo correction and freezing a paying customer over a one-character edit
// is worse than the fraud it prevents. The form says so before you type,
// rather than after an admin gets in touch.
//
// KYC IS NOT DONE HERE. Document capture works on a phone camera; this page
// links to it and shows the resulting tier. Duplicating upload on the web
// would mean two KYC flows to keep in step.
// =============================================================================

import { AlertCircle, Check, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { accountApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { AgentProfile, AgentProfileUpdate } from "@/types/account";

/** Fields whose change lands in the admin KYC review queue once the
 *  account is approved (D-20). Used to decide whether to warn. */
const KYC_BEARING: (keyof AgentProfileUpdate)[] = [
  "full_name",
  "business_name",
  "gstin",
  "pan",
];

const APPROVED_TIERS = new Set([
  "document_verified",
  "physically_verified",
  "premium_verified",
]);

function tierLabel(tier: string): string {
  switch (tier) {
    case "document_pending":
      return "Documents pending review";
    case "document_verified":
      return "Verified";
    case "physically_verified":
      return "Physically verified";
    case "premium_verified":
      return "Premium verified";
    default:
      return tier.replace(/_/g, " ");
  }
}

export function ProfilePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [profile, setProfile] = useState<AgentProfile | null>(null);
  const [form, setForm] = useState<AgentProfileUpdate>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const p = await accountApi.profile(accessToken, signal);
        setProfile(p);
        setForm({
          full_name: p.full_name,
          email: p.email,
          business_name: p.business_name,
          business_address: p.business_address,
          gstin: p.gstin,
          pan: p.pan,
        });
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Could not load profile.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => {
      c.abort();
    };
  }, [load]);

  /** Only the fields the user actually changed.
   *
   * Sending the whole form back would write every field on every save,
   * which the profile audit (migration 0090) would faithfully record as
   * a change to each — burying the one edit that mattered under five
   * that did not happen.
   */
  function changedFields(): AgentProfileUpdate {
    if (profile === null) {
      return {};
    }
    const out: AgentProfileUpdate = {};
    for (const key of Object.keys(form) as (keyof AgentProfileUpdate)[]) {
      const next = form[key] ?? null;
      const prev = profile[key as keyof AgentProfile] ?? null;
      if (next !== prev) {
        // Empty string means "clear it", which the API expects as null.
        (out as Record<string, unknown>)[key] = next === "" ? null : next;
      }
    }
    return out;
  }

  const changed = changedFields();
  const changedKeys = Object.keys(changed) as (keyof AgentProfileUpdate)[];
  const willFlag =
    profile !== null &&
    APPROVED_TIERS.has(profile.kyc_tier) &&
    changedKeys.some((k) => KYC_BEARING.includes(k));

  async function save(): Promise<void> {
    if (accessToken === null || changedKeys.length === 0) {
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await accountApi.updateProfile(accessToken, changed);
      setProfile(updated);
      setSaved(true);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.problem.detail : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  function field(
    key: keyof AgentProfileUpdate,
    label: string,
    placeholder?: string,
  ): React.JSX.Element {
    return (
      <div className="space-y-1">
        <Label htmlFor={key}>{label}</Label>
        <Input
          id={key}
          value={form[key] ?? ""}
          placeholder={placeholder}
          onChange={(e) => {
            setForm((f) => ({ ...f, [key]: e.target.value }));
            setSaved(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Profile" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-3xl space-y-4 p-6">
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>
              {loading
                ? "Loading…"
                : `${profile?.phone ?? ""} · ${tierLabel(profile?.kyc_tier ?? "")}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Phone and KYC documents are managed in the mobile app, where
            document capture works.
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Company details</CardTitle>
            <CardDescription>
              Shown to customers on invoices and used for GST reporting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {field("full_name", "Contact name")}
            {field("email", "Email", "you@example.com")}
            {field("business_name", "Business name")}
            {field("business_address", "Business address")}
            {field("gstin", "GSTIN", "29AABCU9603R1ZX")}
            {field("pan", "PAN", "AABCU9603R")}

            {willFlag && (
              // Said BEFORE saving, not after. An agent who learns from
              // an admin call that changing their GSTIN raised a review
              // will assume something went wrong.
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You&apos;re changing a detail your KYC was approved
                  against. Your account stays active and you can keep
                  working — the change just goes to our team for a quick
                  look.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-3">
              <Button
                disabled={saving || changedKeys.length === 0}
                onClick={() => {
                  void save();
                }}
              >
                {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {changedKeys.length === 0
                  ? "No changes"
                  : `Save ${String(changedKeys.length)} change${changedKeys.length === 1 ? "" : "s"}`}
              </Button>
              {saved && (
                <span className="flex items-center text-sm text-success">
                  <Check className="mr-1 h-4 w-4" /> Saved
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Settlement account</CardTitle>
            <CardDescription>
              Where withdrawals are paid. A verified account is required
              before any withdrawal is released (p68).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (profile?.bank_accounts.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                No account added yet. Add one in the mobile app.
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                {(profile?.bank_accounts ?? []).map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between border-b py-2 last:border-b-0"
                  >
                    <div>
                      <div className="font-medium">
                        {b.bank_name}
                        {b.is_primary && (
                          <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">
                            primary
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        ••••{b.account_number_last_4} · {b.ifsc_code}
                      </div>
                    </div>
                    <span className="text-xs capitalize text-muted-foreground">
                      {b.verification_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
